import "./index.scss";

import * as Sentry from "@sentry/react";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { type PropsWithChildren,StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";

import ErrorBoundary from "./lib/components/other/ErrorBoundary";
import { useMediaQuery } from "./lib/hooks/useMediaQuery";
import { initializeGoogleTagManager } from "./lib/utils/functions/gtm.functions";
import { registerFrenchErrorMap } from "./lib/utils/functions/validation.functions";
import { routeTree } from "./routeTree.gen";

registerFrenchErrorMap();
initializeGoogleTagManager();

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const router = createRouter({
  routeTree,
  defaultPendingMs: 0,
  defaultPreload: "intent",
});

declare module "react" {
  export interface CSSProperties {
    [key: `--${string}`]: string | number;
  }
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const AppTitleProvider = ({ children }: PropsWithChildren) => {
  useEffect(() => {
    switch (import.meta.env.VITE_USER_NODE_ENV) {
      case "staging":
        document.title = "Staging Libe app";
        break;
    }
  }, []);

  return <>{children}</>;
};

const AppToaster = () => {
  const isMobile = useMediaQuery("(max-width: 850px)");

  return <Toaster position={isMobile ? "top-center" : "bottom-right"} />;
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <AppTitleProvider>
        <AppToaster />
        <RouterProvider router={router} />
      </AppTitleProvider>
    </ErrorBoundary>
  </StrictMode>,
);
